import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { logoutUser } from "../../../firebase";
import { motion, AnimatePresence } from "framer-motion";
import PreloadLink from "./PreloadLink";

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, isAdmin } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(true);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = React.useRef(null);

  const calculators = [
    { id: "sip", name: "SIP Calculator", icon: "💰" },
    { id: "ppf", name: "PPF Calculator", icon: "🏦" },
    { id: "ps", name: "Public Provident Fund", icon: "📈" },
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      //setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const getNavItems = () => {
    const baseNavItems = [
      { path: "/", label: "Home" },
      {
        label: "Services",
        items: [
          { path: "/services/sip-planning", label: "SIP Planning", icon: "💰" },
          {
            path: "/services/goal-based-investment",
            label: "Goal-based Investment",
            icon: "🎯",
          },
          {
            path: "/services/retirement-planning",
            label: "Retirement Planning",
            icon: "🏖️",
          },
          {
            path: "/services/child-education",
            label: "Child Education",
            icon: "🎓",
          },
          {
            path: "/services/portfolio-review",
            label: "Portfolio Review",
            icon: "📊",
          },
          {
            path: "/services/tax-saving-funds",
            label: "Tax Saving Funds",
            icon: "💸",
          },
        ],
      },
      // { path: "/government-schemes", label: "Government Schemes" },
      { path: "/calculators", label: "Calculators" },
      {
        path: "https://articles.buildmywealth.net",
        label: "Blogs",
        external: true,
      },
      { path: "/about", label: "About Us" },
      { path: "/contact", label: "Contact Us" },
      // { path: '/articles', label: 'Articles' },
      // { path: '/youtube', label: 'Videos' }
    ];

    if (isAdmin) {
      baseNavItems.splice(baseNavItems.length - 1, 0, {
        label: "Leads",
        items: [
          { path: "/leads/add", label: "Add Lead" },
          { path: "/leads/manage", label: "Manage Leads" },
        ],
      });
    }

    return baseNavItems;
  };

  const navItems = getNavItems();

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate("/");
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setProfileMenuOpen(false);
      }
    }
    if (profileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileMenuOpen]);

  return (
    <nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-sm shadow-lg"
          : location.pathname === "/"
          ? "bg-transparent"
          : "bg-white shadow-lg"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo Section */}
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <PreloadLink
                to="/"
                className={`flex items-center space-x-2 text-xl font-bold transition-colors duration-300 ${
                  isScrolled
                    ? "text-indigo-600"
                    : location.pathname === "/"
                    ? "text-white"
                    : "text-indigo-600"
                }`}
              >
                <img
                  src="/assets/images/BuildMyWealth_Logo.webp"
                  alt="BuildMyWealth Logo"
                  className="h-20 w-24"
                  loading="lazy"
                  width="96"
                  height="80"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/images/BuildMyWealth_Logo.png";
                  }}
                />
                {/* <span>BuildMyWealth</span> */}
              </PreloadLink>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:ml-6 md:flex md:space-x-8">
              {navItems.map((item) =>
                item.items ? (
                  <div key={item.label} className="relative group py-3">
                    <button
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium transition-all duration-300 group-hover:bg-white/10 rounded-md ${
                        (item.label === "Services" &&
                          location.pathname.startsWith("/services")) ||
                        (item.label === "Leads" &&
                          location.pathname.startsWith("/leads"))
                          ? isScrolled
                            ? "text-indigo-600"
                            : location.pathname === "/"
                            ? "text-white"
                            : "text-indigo-600"
                          : isScrolled
                          ? "text-gray-700 hover:text-indigo-600"
                          : location.pathname === "/"
                          ? "text-white/90 hover:text-white"
                          : "text-gray-600 hover:text-indigo-600"
                      }`}
                    >
                      {item.label}
                      <svg
                        className="ml-2 w-4 h-4 transition-transform duration-200 group-hover:rotate-180"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    <div className="absolute left-1/2 transform -translate-x-1/2 mt-1 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-out z-50">
                      <div className="relative bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 overflow-hidden">
                        {item.items.map((subItem) => (
                          <PreloadLink
                            key={subItem.path}
                            to={subItem.path}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors duration-150"
                          >
                            <span className="flex items-center gap-2">
                              {subItem.icon && <span>{subItem.icon}</span>}
                              {subItem.label}
                            </span>
                          </PreloadLink>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : item.external ? (
                  <a
                    key={item.path}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center px-3 py-2 text-sm font-medium transition-all duration-300 ${
                      isScrolled
                        ? "text-gray-700 hover:text-indigo-600"
                        : location.pathname === "/"
                        ? "text-white/90 hover:text-white"
                        : "text-gray-600 hover:text-indigo-600"
                    }`}
                  >
                    {item.label}
                    <svg
                      className="ml-1 w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                ) : (
                  <PreloadLink
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-3 py-2 text-sm font-medium transition-all duration-300 ${
                      location.pathname === item.path
                        ? isScrolled
                          ? "text-indigo-600 border-b-2 border-indigo-600"
                          : location.pathname === "/"
                          ? "text-white border-b-2 border-white"
                          : "text-indigo-600 border-b-2 border-indigo-600"
                        : isScrolled
                        ? "text-gray-700 hover:text-indigo-600"
                        : location.pathname === "/"
                        ? "text-white/90 hover:text-white"
                        : "text-gray-600 hover:text-indigo-600"
                    }`}
                  >
                    {item.label}
                  </PreloadLink>
                )
              )}
            </div>
          </div>

          {/* Auth and Mobile Menu Button */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center gap-4">
              {currentUser ? (
                <div className="relative" ref={profileMenuRef}>
                  <button
                    onClick={() => setProfileMenuOpen((open) => !open)}
                    className={`flex items-center px-3 py-2 rounded-full transition-all duration-300 focus:outline-none ${
                      isScrolled
                        ? "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                        : location.pathname === "/"
                        ? "bg-white/80 text-indigo-700 hover:bg-white"
                        : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                    }`}
                  >
                    <span className="mr-2">
                      {/* Modern user icon */}
                      <svg
                        className="w-6 h-6"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </span>
                    <span className="hidden sm:inline">
                      {currentUser.displayName ||
                        currentUser.email ||
                        currentUser.phoneNumber}
                    </span>
                    <svg
                      className="ml-2 w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <AnimatePresence>
                    {profileMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50"
                      >
                        <div className="py-1">
                          <PreloadLink
                            to="/profile"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                            onClick={() => setProfileMenuOpen(false)}
                          >
                            <svg
                              className="w-5 h-5 text-indigo-500"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                            My Profile
                          </PreloadLink>
                          <PreloadLink
                            to="/risk-profile"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                            onClick={() => setProfileMenuOpen(false)}
                          >
                            <svg
                              className="w-5 h-5 text-indigo-500"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 20V10m0 0l-3 3m3-3l3 3M4 4h16v16H4V4z"
                              />
                            </svg>
                            Risk Profile
                          </PreloadLink>
                          <PreloadLink
                            to="/goals"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                            onClick={() => setProfileMenuOpen(false)}
                          >
                            <svg
                              className="w-5 h-5 text-indigo-500"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M13 16h-1v-4h-1m1-4h.01M12 20c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z"
                              />
                            </svg>
                            Goals
                          </PreloadLink>
                          {isAdmin && (
                            <PreloadLink
                              to="/admin/investment-quiz-responses"
                              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                              onClick={() => setProfileMenuOpen(false)}
                            >
                              <svg
                                className="w-5 h-5 text-indigo-500"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 17v-2a4 4 0 014-4h4"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M17 9V7a4 4 0 00-4-4H7a4 4 0 00-4 4v10a4 4 0 004 4h6a4 4 0 004-4v-2"
                                />
                              </svg>
                              Quiz Responses
                            </PreloadLink>
                          )}

                          {/* <PreloadLink
                            to="/kyc"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                            onClick={() => setProfileMenuOpen(false)}
                          >
                            <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                              <rect x="4" y="4" width="16" height="16" rx="2" strokeWidth="2" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 11h8M8 15h5" />
                            </svg>
                            KYC
                          </PreloadLink>
                          <PreloadLink
                            to="/bank-details"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                            onClick={() => setProfileMenuOpen(false)}
                          >
                            <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10v10a1 1 0 001 1h16a1 1 0 001-1V10M16 21V7a4 4 0 00-8 0v14" />
                            </svg>
                            Bank Account Details
                          </PreloadLink>
                          <PreloadLink
                            to="/bank-mandates"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                            onClick={() => setProfileMenuOpen(false)}
                          >
                            <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                              <rect x="3" y="7" width="18" height="13" rx="2" strokeWidth="2" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 3v4M8 3v4M3 11h18" />
                            </svg>
                            Bank Mandates
                          </PreloadLink> */}
                          <button
                            onClick={async () => {
                              setProfileMenuOpen(false);
                              await handleLogout();
                            }}
                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <svg
                              className="w-5 h-5 text-red-500"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M17 16l4-4m0 0l-4-4m4 4H7"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M3 12h4"
                              />
                            </svg>
                            Logout
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <PreloadLink
                  to="/login"
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                    isScrolled
                      ? "bg-indigo-600 text-white hover:bg-indigo-700"
                      : location.pathname === "/"
                      ? "bg-white text-indigo-600 hover:bg-indigo-50"
                      : "bg-indigo-600 text-white hover:bg-indigo-700"
                  }`}
                >
                  Login
                </PreloadLink>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`md:hidden inline-flex items-center justify-center p-2 rounded-md transition-colors duration-300 ${
                  isScrolled
                    ? "text-gray-600 hover:text-indigo-600"
                    : location.pathname === "/"
                    ? "text-white hover:text-white/80"
                    : "text-gray-600 hover:text-indigo-600"
                }`}
              >
                <span className="sr-only">Open main menu</span>
                {!isMobileMenuOpen ? (
                  <svg
                    className="h-6 w-6"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-6 w-6"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden absolute left-0 right-0 top-16 bg-white shadow-lg overflow-hidden"
            >
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navItems.map((item) =>
                  item.items ? (
                    <div key={item.label}>
                      <button
                        onClick={() =>
                          setMobileServicesOpen(!mobileServicesOpen)
                        }
                        className="w-full flex items-center justify-between px-4 py-2 text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-md"
                      >
                        {item.label}
                        <svg
                          className={`w-5 h-5 transform transition-transform ${
                            mobileServicesOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      <motion.div
                        initial={false}
                        animate={{ height: mobileServicesOpen ? "auto" : 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 py-2 space-y-1">
                          {item.items.map((subItem) => (
                            <PreloadLink
                              key={subItem.path}
                              to={subItem.path}
                              className="block px-4 py-2 text-sm text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-md"
                              onClick={() => {
                                setMobileServicesOpen(false);
                                setIsMobileMenuOpen(false);
                              }}
                            >
                              {subItem.icon && <span>{subItem.icon}</span>}{" "}
                              {subItem.label}
                            </PreloadLink>
                          ))}
                        </div>
                      </motion.div>
                    </div>
                  ) : item.external ? (
                    <a
                      key={item.path}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-2 text-base font-medium text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-md"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="flex items-center justify-between">
                        {item.label}
                        <svg
                          className="ml-1 w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </div>
                    </a>
                  ) : (
                    <PreloadLink
                      key={item.path}
                      to={item.path}
                      className={`block px-4 py-2 text-base font-medium rounded-md ${
                        location.pathname === item.path
                          ? "text-indigo-600 bg-indigo-50"
                          : "text-gray-700 hover:text-indigo-600 hover:bg-indigo-50"
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </PreloadLink>
                  )
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

// Floating Sticky Bottom Button (global, right side, non-blocking)
export const TalkToMukeshButton = () => (
  <a
    href="https://wa.me/919616622620?text=Hi%20Mukesh%2C%20I%20have%20a%20question%20about%20my%20investments."
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-3 right-3 z-50 px-6 py-3 bg-green-600 text-white font-semibold rounded-full shadow-lg hover:bg-green-700 transition-all flex items-center gap-2 md:right-8 md:bottom-4 md:w-auto md:max-w-none w-[95vw] max-w-xs left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 text-base md:text-lg"
    style={{ minWidth: 0, pointerEvents: "auto", boxSizing: "border-box" }}
  >
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
    <span className="truncate">Talk to Mukesh</span>
  </a>
);

export default Navigation;
